import { useEffect, useId } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldErrors } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  TextArea,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import {
  ViolationAlert,
  type ViolationSection,
} from '~/components/bidding/common/SubmitConfirmModal'
import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import type { ApproveFormValues } from '~/pages/AdminApprovalTasks/models/approveSchema'
import { approveFormSchema } from '~/pages/AdminApprovalTasks/models/approveSchema'

type Props = {
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  onConfirm: (comment?: string) => void
  /**
   * 承認を実行できなかった理由 (入札制限違反・自動取消時刻超過、 AC-APT-022 (c)(d))。 空でなければ
   * 本文の上にエラーとして出し、 承認の確定を止める (承認者は札を直せないので、 差戻しで見直しを求める)。
   * ダイアログは開いたまま、 入力したコメントも保持する。
   */
  blockedSections?: readonly ViolationSection[]
}

/**
 * 承認確認ダイアログ。
 * コメントは任意。
 * AC-015: オーバーレイクリックでは閉じない (Modal デフォルト動作)。
 */
export const ApproveDialog = ({
  isOpen,
  isLoading,
  onClose,
  onConfirm,
  blockedSections = [],
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const commentId = useId()

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ApproveFormValues>({
    resolver: zodResolver(approveFormSchema),
  })

  const handleClose = (): void => {
    reset()
    onClose()
  }

  // RejectDialog と同様、 承認も fire-and-forget で失敗時に dialog が閉じない。 onSubmit で
  // reset するとコメント (任意) が失われるため submit では reset せず、 open 契機で reset する。
  const onSubmit = (values: ApproveFormValues): void => {
    onConfirm(values.comment !== '' ? values.comment : undefined)
  }

  // コメントは任意なので現行 schema では validation に失敗しないが、 制約が
  // 増えた時に「承認するボタンが効かない」状態を作らないよう最初から配線しておく。
  const onInvalid = (formErrors: FieldErrors<ApproveFormValues>): void => {
    const alertMessage = resolveFormErrorAlertMessage(
      formErrors,
      t('page:adminApprovalTasks.serverError.validation'),
    )
    if (alertMessage !== undefined) {
      setError('root.serverError', { message: alertMessage })
    } else {
      // 直前の失敗で出した alert が残り続けるのを防ぐ。
      clearErrors('root.serverError')
    }
  }

  useEffect(() => {
    if (isOpen) {
      reset()
    }
  }, [isOpen, reset])

  return (
    <Modal
      footer={
        <SaveCancelFooter
          isCancelDisabled={isLoading}
          isPending={isLoading}
          isSaveDisabled={isLoading || blockedSections.length > 0}
          onCancel={handleClose}
          onSave={() => {
            void handleSubmit(onSubmit, onInvalid)()
          }}
          saveLabel={t('page:adminApprovalTasks.approveDialog.confirm')}
        />
      }
      isOpen={isOpen}
      onClose={handleClose}
      title={t('page:adminApprovalTasks.approveDialog.title')}
    >
      <VStack align="stretch" gap={3}>
        {errors.root?.serverError !== undefined && (
          <Alert status="error" title={errors.root.serverError.message} />
        )}
        {blockedSections.map((section) => (
          <ViolationAlert
            key={section.title}
            section={section}
            status="error"
          />
        ))}
        <p style={{ margin: 0, fontSize: fontSizes.md.value }}>
          {t('page:adminApprovalTasks.approveDialog.body')}
        </p>
        {/* label / error は field slot を通す。 手組みの <label> だと
            label recipe の 14px/600 が当たらず 13px のままになる。
            コメントは現行 schema では任意 (= error は出ない) だが、 制約が増えた時に
            項目直下のエラーが画面のどこにも出ない状態にならないよう slot を置く。 */}
        <FieldRoot invalid={errors.comment !== undefined}>
          <FieldLabel htmlFor={commentId}>
            {t('page:adminApprovalTasks.approveDialog.commentLabel')}
          </FieldLabel>
          <TextArea
            id={commentId}
            placeholder={t(
              'page:adminApprovalTasks.approveDialog.commentPlaceholder',
            )}
            rows={3}
            {...register('comment')}
          />
          {errors.comment !== undefined && (
            <FieldErrorText>{errors.comment.message}</FieldErrorText>
          )}
        </FieldRoot>
      </VStack>
    </Modal>
  )
}
